import { useState, useRef, useEffect } from "react";
import CodeEditor from "../components/CodeEditor";
import "./CompilerPage.css";

const STORAGE_KEY = "TSS_COMPILER_STATE";

const PREDEFINED_CODE = {
  python: `# Write your Python code here\nprint("Hello World")\n`,
  c: `#include <stdio.h>\n\nint main()\n{\n    printf("Hello World");\n    return 0;\n}\n`,
  cpp: `#include <bits/stdc++.h>\nusing namespace std;\n\nint main()\n{\n    cout << "Hello World";\n    return 0;\n}\n`,
  java: `public class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello World");\n    }\n}\n`
};

export default function CompilerPage() {
  const [language, setLanguage] = useState("python");
  const [code, setCode] = useState("");
  const [needsInput, setNeedsInput] = useState(true);

  const [testcases, setTestcases] = useState([{ input: "", output: "" }]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const resultRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      setLanguage(parsed.language || "python");
      setNeedsInput(parsed.needsInput ?? true);
      setTestcases(parsed.testcases || [{ input: "", output: "" }]);
      setCode(parsed.code || PREDEFINED_CODE[parsed.language || "python"]);
    } else {
      setCode(PREDEFINED_CODE.python);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ language, code, needsInput, testcases })
    );
  }, [language, code, needsInput, testcases]);

  useEffect(() => {
    if (!code || code.trim() === "") {
      setCode(PREDEFINED_CODE[language]);
    }
  }, [language]); // eslint-disable-line

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (code.trim()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [code]);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [result]);

  const runCode = async () => {
    setLoading(true);
    setResult(null);

    const BASE_URL = "https://backend.teamtechsign.in";
    const endpoint = needsInput ? "/run" : "/run-code-alone";

    const payload = needsInput
      ? { language, code, testcases }
      : { language, code };

    try {
      const res = await fetch(`${BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      setResult(data);
    } catch(e) {
      console.log("Error while running code:", e);
      setResult({ error: "⚠️ Server not reachable" });
    }

    setLoading(false);
  };

  const updateTestcase = (i, field, value) => {
    const updated = [...testcases];
    updated[i][field] = value;
    setTestcases(updated);
  };

  const removeTestcase = (indexToRemove) => {
    if (testcases.length === 1) return;
    setTestcases(testcases.filter((_, i) => i !== indexToRemove));
  };

  return (
    <div className="pxc-compiler-root">
      <div className="pxc-split-container">
        
        {/* LEFT PANEL */}
        <div className="pxc-panel pxc-left-panel">
          <div className="pxc-panel-header">
            <span className="pxc-header-title">IDE</span>
            <div className="pxc-controls-group">
              <div className="pxc-select-container">
                <select
                  className="pxc-select"
                  value={language}
                  onChange={(e) => {
                    setLanguage(e.target.value);
                    setCode("");
                  }}
                >
                  <option value="python">Python</option>
                  <option value="c">C</option>
                  <option value="cpp">C++</option>
                  <option value="java">Java</option>
                </select>
                <span className="pxc-select-caret">▼</span>
              </div>

              <div className="pxc-toggle-container">
                <span className="pxc-toggle-label">Needs Input</span>
                <label className="pxc-switch">
                  <input
                    type="checkbox"
                    checked={needsInput}
                    onChange={() => setNeedsInput(!needsInput)}
                  />
                  <span className="pxc-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="pxc-editor-area">
            <CodeEditor language={language} code={code} setCode={setCode} />
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="pxc-panel pxc-right-panel">
          <div className="pxc-panel-header pxc-action-bar">
            <span className="pxc-header-title">CONSOLE & RESULTS</span>
            <button className="pxc-run-button" onClick={runCode} disabled={loading}>
              <span className="pxc-play-icon">▶</span> {loading ? "Running..." : "Run Code"}
            </button>
          </div>

          <div className="pxc-panel-body">
            {result ? (
              <div className="pxc-result-wrapper" ref={resultRef}>
                {result.output && (
                  <div className="pxc-output-box pxc-success">
                    <span className="pxc-box-label">Output</span>
                    <pre>{result.output}</pre>
                  </div>
                )}

                {result.error && (
                  <div className="pxc-output-box pxc-error">
                    <span className="pxc-box-label">Compilation / Runtime Error</span>
                    <pre>{result.error}</pre>
                  </div>
                )}

                {result.passed !== undefined && result.total !== undefined && (
                  <div className="pxc-summary-pill">
                    Passed: {result.passed} / {result.total}
                  </div>
                )}

                {result.results && (
                  <div className="pxc-testcase-results-container">
                    <span className="pxc-section-label">TESTCASE RESULTS</span>
                    {result.results.map((tc, index) => (
                      <div
                        key={index}
                        className={`pxc-testcase-card ${
                          tc.status === "PASS" ? "pxc-card-pass" : "pxc-card-fail"
                        }`}
                      >
                        <div className="pxc-tc-card-header">
                          <span className="pxc-tc-title">Testcase #{index + 1}</span>
                          <span
                            className={
                              tc.status === "PASS" ? "pxc-status-pass" : "pxc-status-fail"
                            }
                          >
                            {tc.status}
                          </span>
                        </div>

                        <div className="pxc-tc-card-body">
                          <p><strong>Input:</strong> {tc.input || ""}</p>
                          <p><strong>Expected:</strong> {tc.expected || ""}</p>
                          <p><strong>Actual:</strong> {tc.actual || ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="pxc-placeholder">
                <p>Run your code to see outputs and testcase executions here.</p>
              </div>
            )}

            {needsInput && (
              <div className="pxc-testcase-config-section">
                <div className="pxc-testcase-config-header">
                  <span className="pxc-section-label">TEST CASES</span>
                  <button
                    type="button"
                    className="pxc-add-btn"
                    onClick={() =>
                      setTestcases([...testcases, { input: "", output: "" }])
                    }
                  >
                    + Add
                  </button>
                </div>

                <div className="pxc-testcase-input-list">
                  {testcases.map((tc, i) => (
                    <div className="pxc-input-card" key={i}>
                      <div className="pxc-input-card-header">
                        <span>Case #{i + 1}</span>
                        {testcases.length > 1 && (
                          <button
                            type="button"
                            className="pxc-remove-btn"
                            onClick={() => removeTestcase(i)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <div className="pxc-input-card-fields">
                        <textarea
                          placeholder="Input"
                          value={tc.input}
                          onChange={(e) =>
                            updateTestcase(i, "input", e.target.value)
                          }
                        />
                        <textarea
                          placeholder="Expected Output"
                          value={tc.output}
                          onChange={(e) =>
                            updateTestcase(i, "output", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}